import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Dribbble, Github, Globe, Plus, X } from "lucide-react";
import { colors } from "@tabbio/tokens";
import { isValidEmail, isValidPhoneForCountry } from "@tabbio/utils";
import {
  collapseCvLinkInput,
  composeCvLink,
  getCvLinkInputPrefix,
  getCvLinkInputValue,
  validateCvLinkInput,
  type CvLinkKey,
} from "@tabbio/utils/cv-profile-links";
import { useAuth } from "../../hooks/useAuth";
import { useUaePassStatus } from "../../hooks/useUaePassStatus";
import { useCVData } from "../../lib/cv-mock-data";
import { WebInput, WebSaveButton } from "../cv/forms/web-form-elements";
import { WebPhoneInput } from "../cv/forms/web-pickers";
import { BasicsInfoDialog, type BasicsInfoDialogRow } from "./BasicsInfoDialog";
import { useOnboarding } from "./OnboardingProvider";
import { OnboardingFooterNote, OnboardingWebStep } from "./web-step-chrome";
import { confirmDeleteCvItem } from "../cv/forms/delete-confirmation";
import { LinkedinIcon } from "../common/BrandIcons";

interface LinkPlatform {
  key: CvLinkKey;
  label: string;
  prefix: string;
  placeholder: string;
  icon: (color: string) => ReactNode;
  brandColor: string;
}

const LINK_PLATFORMS: LinkPlatform[] = [
  {
    key: "website",
    label: "Website",
    prefix: getCvLinkInputPrefix("website"),
    placeholder: "yoursite.com",
    icon: (color) => <Globe size={15} color={color} />,
    brandColor: colors.primary,
  },
  {
    key: "github",
    label: "GitHub",
    prefix: getCvLinkInputPrefix("github"),
    placeholder: "username",
    icon: (color) => <Github size={15} color={color} />,
    brandColor: "#191717",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    prefix: getCvLinkInputPrefix("linkedin"),
    placeholder: "username",
    icon: (color) => <LinkedinIcon size={15} color={color} />,
    brandColor: "#0a66c2",
  },
  {
    key: "dribbble",
    label: "Dribbble",
    prefix: getCvLinkInputPrefix("dribbble"),
    placeholder: "username",
    icon: (color) => <Dribbble size={15} color={color} />,
    brandColor: "#ea4c89",
  },
  {
    key: "behance",
    label: "Behance",
    prefix: getCvLinkInputPrefix("behance"),
    placeholder: "username",
    icon: (color) => <Briefcase size={15} color={color} />,
    brandColor: "#1769ff",
  },
];

export function StepHowToReachYou() {
  const { t } = useTranslation("auth");
  const { t: tSeeker } = useTranslation("seeker");
  const { user, isUaePassVerified } = useAuth();
  const { data: uaePassStatus } = useUaePassStatus(isUaePassVerified);
  const { profile, updateProfile } = useCVData();
  const { profileData, nextStep, prevStep } = useOnboarding();
  const synced = useRef(false);

  const [email, setEmail] = useState(
    (profile.contactEmail || user?.email || "").trim(),
  );
  const [emailError, setEmailError] = useState("");
  const [phone, setPhone] = useState(profileData.phone || profile.phone);
  const [phoneCountry, setPhoneCountry] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState("");

  // UAE PASS contact values are only fallbacks for a new CV. The CV contact
  // details remain editable and are separate from the verified account record.
  const uaePassEmail = (uaePassStatus?.uaePassData?.email ?? "").trim();
  const uaePassMobile = (uaePassStatus?.uaePassData?.mobile ?? "").trim();

  // Backfill provider values only while the CV draft is empty; never overwrite
  // a saved or user-entered CV contact value.
  useEffect(() => {
    if (!email.trim() && uaePassEmail) setEmail(uaePassEmail);
    if (!phone.trim() && uaePassMobile) setPhone(uaePassMobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uaePassEmail, uaePassMobile]);
  const [howOpen, setHowOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [links, setLinks] = useState<Record<CvLinkKey, string | undefined>>({
    website: getCvLinkInputValue("website", profile.website || undefined),
    github: getCvLinkInputValue("github", profile.github || undefined),
    linkedin: getCvLinkInputValue("linkedin", profile.linkedin || undefined),
    dribbble: getCvLinkInputValue("dribbble", profile.dribbble || undefined),
    behance: getCvLinkInputValue("behance", profile.behance || undefined),
  });
  const [linkErrors, setLinkErrors] = useState<
    Partial<Record<CvLinkKey, string>>
  >({});

  useEffect(() => {
    if (synced.current) {
      return;
    }
    if (!(profile.phone || profile.linkedin || profile.website)) {
      return;
    }

    synced.current = true;
    if (profile.phone) {
      setPhone(profile.phone);
    }
    setLinks((prev) => ({
      ...prev,
      ...(profile.website
        ? { website: getCvLinkInputValue("website", profile.website) }
        : {}),
      ...(profile.github
        ? { github: getCvLinkInputValue("github", profile.github) }
        : {}),
      ...(profile.linkedin
        ? { linkedin: getCvLinkInputValue("linkedin", profile.linkedin) }
        : {}),
      ...(profile.dribbble
        ? { dribbble: getCvLinkInputValue("dribbble", profile.dribbble) }
        : {}),
      ...(profile.behance
        ? { behance: getCvLinkInputValue("behance", profile.behance) }
        : {}),
    }));
  }, [profile]);

  const linkErrorMessage = useCallback(
    (key: CvLinkKey): string =>
      key === "website"
        ? t("onboarding.howToReachYou.websiteError", {
            defaultValue: "Enter a website, e.g. yoursite.com",
          })
        : t("onboarding.howToReachYou.linkUsernameError", {
            defaultValue: "Enter your username or paste your profile link.",
          }),
    [t],
  );

  const setLink = useCallback(
    (key: CvLinkKey, input: string) => {
      // A pasted full link collapses to the handle after the fixed prefix.
      const value = collapseCvLinkInput(key, input);
      setLinks((prev) => ({ ...prev, [key]: value }));
      const code = validateCvLinkInput(key, value);
      setLinkErrors((prev) => ({
        ...prev,
        [key]: code ? linkErrorMessage(key) : undefined,
      }));
    },
    [linkErrorMessage],
  );

  const toggleLink = (key: CvLinkKey) => {
    if (links[key] === undefined) {
      setLinks((prev) => ({ ...prev, [key]: "" }));
      setLinkErrors((prev) => ({ ...prev, [key]: undefined }));
      return;
    }

    void confirmDeleteCvItem(tSeeker, "link", () => {
      setLinks((prev) => ({ ...prev, [key]: undefined }));
      setLinkErrors((prev) => ({ ...prev, [key]: undefined }));
    });
  };

  const addedLinks = LINK_PLATFORMS.filter(
    (platform) => links[platform.key] !== undefined,
  );

  const handleContinue = async () => {
    const trimmedEmail = email.trim();
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      setEmailError(
        t("onboarding.howToReachYou.emailInvalid", {
          defaultValue: "Enter a valid email address.",
        }),
      );
      return;
    }

    // The phone is optional (the API's `optionalPhoneSchema` already allows
    // an empty one); only a number that was entered has to be valid.
    const trimmedPhone = phone.trim();
    if (trimmedPhone && !isValidPhoneForCountry(trimmedPhone, phoneCountry)) {
      setPhoneError(
        t("onboarding.cvJourney.phoneInvalid", {
          defaultValue: "Enter a valid phone number for the selected country.",
        }),
      );
      return;
    }

    const nextErrors: Partial<Record<CvLinkKey, string>> = {};
    for (const platform of addedLinks) {
      if (validateCvLinkInput(platform.key, links[platform.key] || "")) {
        nextErrors[platform.key] = linkErrorMessage(platform.key);
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setLinkErrors(nextErrors);
      return;
    }

    setEmailError("");
    setPhoneError("");
    setLinkErrors({});
    updateProfile({
      contactEmail: trimmedEmail,
      phone: trimmedPhone,
      website: composeCvLink("website", links.website),
      github: composeCvLink("github", links.github),
      linkedin: composeCvLink("linkedin", links.linkedin),
      dribbble: composeCvLink("dribbble", links.dribbble),
      behance: composeCvLink("behance", links.behance),
    });

    setIsSubmitting(true);
    try {
      await nextStep();
    } finally {
      setIsSubmitting(false);
    }
  };

  const infoRows: BasicsInfoDialogRow[] = [
    {
      question: t("onboarding.howToReachYou.modalRow1Q"),
      answer: t("onboarding.howToReachYou.modalRow1A"),
    },
    {
      question: t("onboarding.howToReachYou.modalRow2Q"),
      answer: t("onboarding.howToReachYou.modalRow2A"),
    },
    {
      question: t("onboarding.howToReachYou.modalRow3Q"),
      answer: t("onboarding.howToReachYou.modalRow3A"),
    },
  ];

  return (
    <OnboardingWebStep
      titleBase={t("onboarding.howToReachYou.titleBase", {
        defaultValue: "How to",
      })}
      titleAccent={t("onboarding.howToReachYou.titleAccent", {
        defaultValue: "reach you",
      })}
      subtitle={t("onboarding.howToReachYou.subtitle", {
        defaultValue: "Only you and employers see these",
      })}
      onBack={prevStep}
    >
      <WebInput
        label={t("onboarding.howToReachYou.emailLabel")}
        value={email}
        onChange={(value) => {
          setEmail(value);
          const trimmed = value.trim();
          if (!trimmed || isValidEmail(trimmed)) setEmailError("");
        }}
        placeholder={t("onboarding.howToReachYou.emailPlaceholder", {
          defaultValue: "example@gmail.com",
        })}
        type="email"
        autoComplete="email"
        readOnly={false}
        error={emailError || undefined}
      />

      <div style={{ marginTop: 12 }}>
        <WebPhoneInput
          value={phone}
          onChange={(value) => {
            setPhone(value);
            const trimmed = value.trim();
            if (!trimmed || isValidPhoneForCountry(trimmed, phoneCountry)) {
              setPhoneError("");
            }
          }}
          onCountryChange={setPhoneCountry}
          label={t("onboarding.howToReachYou.phoneLabel", {
            defaultValue: "Phone number (optional)",
          })}
          inputId="onboarding-phone"
          describedBy={phoneError ? "onboarding-phone-error" : undefined}
          invalid={Boolean(phoneError)}
          disabled={false}
        />
        {phoneError ? (
          <p id="onboarding-phone-error" style={errorTextStyle}>
            {phoneError}
          </p>
        ) : null}
      </div>

      <section style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          <span style={sectionTitleStyle}>
            {t("onboarding.howToReachYou.linksSection")}
          </span>
          <span style={helperTextStyle}>
            {t("onboarding.howToReachYou.linksSublabel")}
          </span>
        </div>
        <div style={pillRowStyle}>
          {LINK_PLATFORMS.map((platform) => {
            const added = links[platform.key] !== undefined;
            return (
              <LinkPill
                key={platform.key}
                platform={platform}
                added={added}
                onToggle={() => toggleLink(platform.key)}
              />
            );
          })}
        </div>

        {addedLinks.map((platform) => (
          <LinkUsernameField
            key={platform.key}
            platform={platform}
            value={links[platform.key] || ""}
            error={linkErrors[platform.key]}
            onChange={(value) => setLink(platform.key, value)}
          />
        ))}
      </section>

      <OnboardingFooterNote
        text={t("onboarding.howToReachYou.footerText")}
        linkLabel={t("onboarding.howToReachYou.howItWorksLink")}
        onLinkClick={() => setHowOpen(true)}
      />
      <WebSaveButton
        label={t("onboarding.howToReachYou.cta")}
        onClick={handleContinue}
        loading={isSubmitting}
      />

      <BasicsInfoDialog
        open={howOpen}
        onClose={() => setHowOpen(false)}
        title={t("onboarding.howToReachYou.modalTitle")}
        doneLabel={t("onboarding.howToReachYou.modalDone")}
        rows={infoRows}
      />
    </OnboardingWebStep>
  );
}

/** Toggleable platform chip: selected = solid neutral fill (WebPillSelector idiom). */
function LinkPill({
  platform,
  added,
  onToggle,
}: {
  platform: LinkPlatform;
  added: boolean;
  onToggle: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      aria-pressed={added}
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...pillStyle,
        ...(added
          ? {
              backgroundColor: "var(--text-primary)",
              borderColor: "var(--text-primary)",
              color: "var(--bg)",
            }
          : hovered
            ? { backgroundColor: "var(--hover-strong, rgba(0,0,0,0.04))" }
            : null),
      }}
    >
      <span style={{ ...pillIconStyle, backgroundColor: platform.brandColor }}>
        {platform.icon("#ffffff")}
      </span>
      <span>{platform.label}</span>
      {added ? <X size={14} /> : <Plus size={14} />}
    </button>
  );
}

/** Pill input with a static LTR prefix: INPUT_BASE recipe with label above. */
function LinkUsernameField({
  platform,
  value,
  error,
  onChange,
}: {
  platform: LinkPlatform;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={labelStyle}>{platform.label}</label>
      <div
        style={{
          ...linkFieldPillStyle,
          borderColor: error
            ? "var(--color-danger, #dc2626)"
            : focused
              ? "var(--text-primary)"
              : "var(--input-border)",
        }}
      >
        <span style={linkPrefixStyle}>{platform.prefix}</span>
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={platform.placeholder}
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          style={linkInputStyle}
        />
      </div>
      {error ? <p style={errorTextStyle}>{error}</p> : null}
    </div>
  );
}

const errorTextStyle: CSSProperties = {
  margin: "6px 0 0",
  paddingInline: 4,
  color: "var(--color-danger, #dc2626)",
  fontSize: 12,
  lineHeight: "16px",
};

const sectionStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  marginTop: 24,
};

const sectionHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 10,
  paddingInline: 4,
};

const sectionTitleStyle: CSSProperties = {
  color: "var(--text-primary)",
  fontSize: 16,
  fontWeight: 600,
};

const helperTextStyle: CSSProperties = {
  color: "var(--text-tertiary)",
  fontSize: 13,
};

const pillRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const pillStyle: CSSProperties = {
  minHeight: 36,
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "4px 10px 4px 5px",
  color: "var(--text-primary)",
  border: "1px solid var(--input-border)",
  borderRadius: 9999,
  backgroundColor: "var(--input-bg)",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  transition: "background-color 150ms, border-color 150ms, color 150ms",
};

const pillIconStyle: CSSProperties = {
  width: 26,
  height: 26,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  flexShrink: 0,
};

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: 8,
  display: "block",
  fontFamily: "inherit",
};

const linkFieldPillStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  width: "100%",
  height: 52,
  borderRadius: 9999,
  borderWidth: 1,
  borderStyle: "solid",
  padding: "0 22px",
  backgroundColor: "var(--input-bg)",
  boxShadow: "var(--gem-input-shadow)",
  boxSizing: "border-box",
  transition: "border-color 150ms",
  direction: "ltr",
};

const linkPrefixStyle: CSSProperties = {
  color: "var(--text-tertiary)",
  fontSize: 15,
  flexShrink: 0,
};

const linkInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: 0,
  border: 0,
  outline: 0,
  color: "var(--text-primary)",
  background: "transparent",
  fontFamily: "inherit",
  fontSize: 15,
  direction: "ltr",
};
